import {parseInteger} from '@benchling/js-utils';
import {captureException} from '@sentry/browser';
import $ from 'jquery';
import _ from 'underscore';

import BenchlingConfig from 'BenchlingConfig';
import * as benchlingUtil from 'BenchlingUtil';
import {Batch, StorableSummary} from 'constants/SampleConstants';
import {BatchInfo, TargetEntry, TargetFile, TargetStorable} from 'folder-items/FileConstants';
import {count} from 'metrics/Metrics';
import {Selector} from 'notebook/tables/excess/Evaluator';
import * as TableConstants from 'notebook/tables/TableConstants';
import TableUtil from 'notebook/tables/TableUtil';
import {notifyTextHasBeenLocalized} from 'util/LocaleAwareParseText';
import sanitizeLink from 'util/sanitizeLink';
import {Optional} from 'util/Types';

import parseDOMColor from './parseDOMColor';
import parseText from './parseText';
import {NonTableWYCIWYPPart} from './WyciwypConstants';

// WHAT YOU C(OPY) IS WHAT YOU P(ASTE)

const {TABLE_MIN_COLUMNS, TABLE_MAX_COLUMNS} = TableConstants;

// Returns the next availale index in row `i` after column `j`.
// Returns newCells[i].length as the next index if the row is filled.
function _nextAvailableIndexInRow(
  i: number,
  j: number,
  newCells: Array<Array<TableConstants.PastedCell>>
): number {
  for (let t = j; t < newCells[i].length; t++) {
    if (newCells[i][t] == null) {
      return t;
    }
  }
  return newCells[i].length;
}

// Pads out `newCells` to width `widestRow` (which is precomputed before this method is called).
// All missing cells in the center are also filled in.
// Note that the first row is padded from the beginning, while other rows
// are padded at the end.
function padTable(newCells: Array<Array<TableConstants.PastedCell>>, widestRow: number): void {
  let j;
  for (let i = 0; i < newCells.length; i++) {
    if (i === 0 && newCells[i].length !== widestRow) {
      const missingSectionLength = widestRow - newCells[i].length;
      const missingSection = new Array(missingSectionLength).map(() => ({text: ''}));
      newCells[i] = missingSection.concat(newCells[i]);
    }
    for (j = 0; j < widestRow; j++) {
      if (newCells[i][j] == null) {
        newCells[i][j] = {text: ''};
      }
    }
  }
}

function filteredAlign(align: string): TableConstants.FormulaAlignment | null {
  switch (align) {
    case 'left':
    case 'right':
    case 'center':
      return align;
    default:
      return null;
  }
}

/**
 * Builds an n x m array of cells given `cells` (some of which may be merged cells).
 * @param  {Array} cells       parsed table cells that were coppied
 * @return {Object}
 *              cells           the new n x m array of cells
 * According to https://www.w3.org/TR/html401/struct/tables.htm :
 *   R1. The number of rows in a table is equal to the number of TR elements contained by the TABLE element.
 *   R2. The number of columns is equal to the number of columns required by the row with the most columns,
 *       including cells that span multiple columns.
 *   R3. For any row that has fewer than this number of columns, the end of that row should be padded with
 *       empty cells.
 *
 * General approach:
 *    1. Iterate through each row of `cells`. Put each element in the next available spot
 *       in `newCells` in the current row. For merged cells, fill in with non-rendered cells.
 *    2. Fill in missing cells of `newCells` with default cells.
 */
function findUnmergedTableCells(
  cells: Array<Array<TableConstants.PastedCell>>
): Array<Array<TableConstants.PastedCell>> | null {
  // if cells are empty, return a 1x1 table
  if (cells.length === 0) {
    return [[{text: ''}]];
  }

  const newCells: Array<Array<TableConstants.PastedCell>> = cells.map((row) => []); // the number of rows is exactly the number of TRs (R1)

  let numberOfColumns = 0;
  for (let i = 0; i < cells.length; i++) {
    let currActualCol = 0;
    for (let j = 0; j < cells[i].length; j++) {
      // The next cell is not trivially located at newCells[i][j], as the previous rows may have a rowSpan
      // that bleeds into this row, or a cell to the left has a colSpan that has already used up the typical
      // location of the cell. So, we keep scanning right until we hit an unoccupied cell (R2).
      currActualCol = _nextAvailableIndexInRow(i, currActualCol, newCells);
      const cell = cells[i][j];
      const rowSpan = cell.mergeRowSpan != null ? cell.mergeRowSpan : 1;
      const colSpan = cell.mergeColSpan != null ? cell.mergeColSpan : 1;

      // Stop processing more cells if this row exceeds the maximum width.
      if (colSpan + currActualCol > TABLE_MAX_COLUMNS) {
        break;
      }

      // Each cell in `cells` occupies a [rowSpan x colSpan] box in `newCells`:
      for (let s = i; s < i + rowSpan; s++) {
        for (let t = currActualCol; t < currActualCol + colSpan; t++) {
          // If we don't have the right number of rows, this table is invalid.
          if (newCells[s] == null) {
            return null;
          }
          // If we are overwriting another value, this table is invalid.
          if (newCells[s][t] != null) {
            return null;
          }
          // Fill in merged cell in newCells
          if (s === i && t === currActualCol) {
            newCells[s][t] = cell;
          } else {
            newCells[s][t] = {text: ''};
          }
        }
      }
      currActualCol += colSpan;
    }
    numberOfColumns = Math.max(numberOfColumns, currActualCol); // (R2)
  }

  padTable(newCells, numberOfColumns); // All rows should be padded to have `numberOfColumns` (R3)
  return newCells;
}

function silentJSONParse(jsonString: string): unknown {
  try {
    return JSON.parse(jsonString);
    // eslint-disable-next-line benchlint/always-throw-from-catch
  } catch (e) {
    // Silently skip if can't parse.
    captureException(e);
    return undefined;
  }
}

export interface ParseTableOptions {
  sanitizeAndParseTD?: (
    td: HTMLTableDataCellElement,
    originalText: string
  ) => {formula?: string; link?: string | null; ignoreColspan?: boolean};
  ignoreTableMinHeight?: boolean;
  ignoreTableMinWidth?: boolean;
  tableType?: Optional<string>;
  /**
   * Set to `true` to explicitly disable locale-aware parsing for this table (e.g. Excel processor,
   * which has its own locale handling). When unset (`undefined`), locale-aware parsing runs for
   * external tables (no `data-excess`). Internal Benchling tables are always skipped via the
   * `data-excess` attribute check inside `parseTable`.
   */
  skipLocaleParsing?: boolean;
}

/**
 * Parses a <table> element into an Object that is compatible with the ELN's table storage format
 * @param {HTMLTableElement} el
 * @param {Object} options - ignoreTableMinWidth:
 * @param {Boolean} options.ignoreTableMinWidth - True to avoid requiring that a table has at least 1 column
 * @param {Function} [options.sanitizeAndParseTD] - function that performs preprocessing on the <td> node,
 *   and returns {link, selector, precision, formula}
 * @param {string} options.tableType - The table type of the table, used to determine if it is a structured table
 * @return {Object} a table object that validates against TableConstants.table
 */
function parseTable(el: HTMLTableElement, options: ParseTableOptions | null): TableConstants.Table | null {
  if (options == null) {
    options = {};
  }
  let cells: Array<Array<TableConstants.PastedCell>> | null = [];
  let width = 0;
  let didAnyCellsGetParsed = false;
  // Some DOMParsers (jsdom's for instance) allow <tr> to be direct children of <table>
  // which isn't valid HTML.
  // '>' is a hack that selects for table > tr which covers this case
  for (const section of ['thead', 'tbody', 'tfoot', '>']) {
    for (const tr of Array.from($(el).find(`${section} tr`))) {
      const row = [];
      for (const td of Array.from($(tr).find('td, th'))) {
        const $td = $(td);

        let formula;
        let important;
        let scientificNotation;
        let link;
        let itemLinks;
        let schemaFieldSelectorOptionIds;
        let typedValue;
        let parentStorableSummary;
        let batch;
        let precision;
        let selector;
        let externalCache;
        let ignoreColspan;

        // The table is already parsed if it originated from within Benchling — skip locale parsing
        // in that case. For external table pastes (no data-excess attr), we want locale-aware
        // parsing to run, so skipLocaleParsing should be false (or unset). Only skip when the
        // caller explicitly passes skipLocaleParsing: true, or when the table has data-excess.
        const skipLocaleParsing: boolean =
          options.skipLocaleParsing === true || el.hasAttribute('data-excess');

        const {fmtTexts, didGetParsedWithLocale: didCellGetParsedWithLocale} = parseText(td, {
          tableType: options.tableType,
          skipLocaleParsing,
        });
        // Extract plain text from the HTML contents of the <TD>
        const text: string = fmtTexts.map((fmtText: NonTableWYCIWYPPart) => fmtText.text.trim()).join('\n');

        // Extract excess cell data created via makeTableHTML. By requiring data-excess on the
        // <table>, we avoid the odd case where Excel preserves data attributes across import / export
        // despite the cell contents changing.
        if (options.sanitizeAndParseTD) {
          ({link, formula, ignoreColspan} = options.sanitizeAndParseTD(td as HTMLTableDataCellElement, text));
        } else if (el.hasAttribute('data-excess')) {
          link = td.getAttribute('data-link');
          itemLinks = td.getAttribute('data-item-links');
          schemaFieldSelectorOptionIds = td.getAttribute('data-sfs-options');
          typedValue = td.getAttribute('data-typed-value');
          parentStorableSummary = td.getAttribute('data-parent-storable-summary');
          batch = td.getAttribute('data-batch');
          selector = td.getAttribute('data-selector');
          precision = td.getAttribute('data-precision');
          important = td.getAttribute('data-important');
          scientificNotation = td.getAttribute('data-scientific-notation');
          externalCache = td.getAttribute('data-external-cache');
          formula = td.getAttribute('data-excess-formula');
        }

        const cellIsParsedAndNotFormula =
          didCellGetParsedWithLocale && (formula == null || !TableUtil.isValidFormula(formula));
        // If the <td> only contains an <a> (ignoring empty whitespace), use its href for the link
        const nonEmptyNodes = $td.contents().filter(function _nonEmptyFilter(): boolean {
          // @ts-expect-error
          return this.nodeType !== Node.TEXT_NODE || !/^\s*$/.test(this.nodeValue);
        });
        const anchor = nonEmptyNodes.length === 1 ? nonEmptyNodes.filter('a')[0] : null;
        if (!link && anchor) {
          link = anchor.getAttribute('href');
        }

        // Extract standard formatting
        const bold = ['bold', 'bolder', '600', '700', '800', '900'].includes($td.css('font-weight'));
        const italic = $td.css('font-style') === 'italic';
        const textDecorations = $td.css('text-decoration').split(/\s+/);
        const underline = textDecorations.includes('underline');
        const strikethrough = textDecorations.includes('line-through');
        const align = filteredAlign($td.css('text-align')) || filteredAlign($td.attr('align'));
        const foregroundColor = parseDOMColor($td.css('color'));
        const backgroundColor = parseDOMColor($td.css('background-color'));
        const rowspan = $td.attr('rowspan');
        const colspan = $td.attr('colspan');

        const cell: TableConstants.PastedCell = {text};

        if (bold) {
          cell.bold = true;
        }
        if (underline) {
          cell.underline = true;
        }
        if (italic) {
          cell.italic = true;
        }
        if (strikethrough) {
          cell.strikethrough = true;
        }
        if (align) {
          cell.align = align;
        }
        if (foregroundColor != null && foregroundColor !== '#000000') {
          cell.foregroundColor = foregroundColor;
        }
        if (backgroundColor != null) {
          cell.backgroundColor = backgroundColor;
        }
        if (rowspan != null) {
          cell.mergeRowSpan = parseInt(rowspan, 10);
        }
        if (colspan != null && !ignoreColspan) {
          cell.mergeColSpan = parseInt(colspan, 10);
        }
        if (formula != null) {
          cell.formula = formula;
          if (BenchlingConfig.ELN_ENABLE_LOCALE_AWARE_PARSING_FOR_COPY_PASTE && cellIsParsedAndNotFormula) {
            cell.formula = text;
          }
        }
        if (link != null && sanitizeLink(link) != null) {
          cell.link = sanitizeLink(link);
        }
        if (itemLinks != null) {
          cell.itemLinks = silentJSONParse(itemLinks) as
            | Array<BatchInfo>
            | Array<TargetFile>
            | Array<TargetEntry>
            | Array<TargetStorable>
            | null;
        }
        if (schemaFieldSelectorOptionIds != null) {
          cell.schemaFieldSelectorOptionIds = silentJSONParse(schemaFieldSelectorOptionIds) as Array<string>;
        }
        if (typedValue != null) {
          cell.typedValue = silentJSONParse(typedValue) as TableConstants.TypedValue | null;
        }
        if (parentStorableSummary != null) {
          cell.parentStorableSummary = silentJSONParse(parentStorableSummary) as StorableSummary;
        }
        if (batch != null) {
          cell.batch = silentJSONParse(batch) as Batch;
        }
        if (selector != null) {
          cell.selector = silentJSONParse(selector) as Selector;
        }
        if (precision != null) {
          cell.precision = parseInteger(precision);
        }
        if (important != null) {
          cell.important = true;
        }
        if (scientificNotation != null) {
          cell.scientificNotation = true;
        }
        if (externalCache != null) {
          cell.externalCache = silentJSONParse(externalCache) as TableConstants.ExternalCache;
        }
        row.push(cell);
        if (cellIsParsedAndNotFormula) {
          didAnyCellsGetParsed = true;
        }
        if (ignoreColspan) {
          // push colspan-1 empty cells into the row to account for the ignored colspan
          let colspanValue = parseInt(colspan, 10);
          while (colspanValue > 1) {
            row.push({text: ''});
            colspanValue--;
          }
        }
      }
      width = Math.max(row.length, width);
      cells.push(row);
    }
  }

  if (BenchlingConfig.ELN_ENABLE_LOCALE_AWARE_PARSING_FOR_COPY_PASTE && didAnyCellsGetParsed) {
    count('bnch.localization.copy_paste_count');
    notifyTextHasBeenLocalized();
  }

  // expand the table according to merged cells
  cells = findUnmergedTableCells(cells);
  if (cells == null) {
    return null;
  }
  cells = cells.filter((row) => row.length > 0);
  // Case where table has a row with no cells
  if (!cells.length) {
    return null;
  }

  width = cells[0].length;

  // Ensure table is rectangular and within bounds
  const minWidth = options.ignoreTableMinWidth ? 1 : TABLE_MIN_COLUMNS;
  const targetWidth = Math.max(Math.min(width, TABLE_MAX_COLUMNS), minWidth);

  const columnDefaults = _.mapObject(TableConstants.COLUMN_FIELD_DEFAULTS, (defaultValue, field) =>
    benchlingUtil.repeat([defaultValue], targetWidth)
  ) as {
    widths: Array<number>;
    columnConstraints: Array<TableConstants.ColumnConstraint | null>;
    columnLabels: Array<string | null>;
  };

  // eslint-disable-next-line no-restricted-syntax
  return _.extend(columnDefaults, {
    table: cells,
    headerCol: $(el).hasClass('benchling-header-col'),
    headerRow: $(el).find('thead tr').length > 0 || $(el).hasClass('benchling-header-row'),
  });
}

export default parseTable;
